import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useStore } from '@stores/rootStoreContext';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { Box, LinearProgress, Typography, Tabs, Tab } from '@mui/material';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ConfirmationModal } from '@components/UI/ConfirmationModal/ConfirmationModal';
import { FigureVersion, Publication, PublicationStatus, StoryStatus, IncutStatus, StoriesIncutBlock } from '@graphql/types';
import RelatedPublicationItem from '@components/UI/RelatedPublications/RelatedPublicationItem/RelatedPublicationItem';
import StoryIncutContent from '../../StoryIncuts/StoryIncutContent/StoryIncutContent';
import { grey50 } from '@theme/vars';
import RHFSearchPublication from '../../UI/RHFSearchPublication/RHFSearchPublication';
import Header from '../../Common/Header/Header';
import Editor from '../../Common/Editor/Editor';
import Views from '@components/Common/Editor/Views';
import { StoryEditorFormProps } from '../../../types/story/StoryEditorFormProps';
import {
  useAddPublicationsToStoryMutation,
  useCreateNewStoryMutation,
  usePublicationsByStoryUidQuery,
  useRemovePublicationsFromStoryMutation,
  useUpdateStoryMutation,
} from './gql/StoryEditor.generated';
import { useStoriesIncutSelectionQuery } from '../../StoryIncuts/StoryIncutContent/gql/StoryIncutContent.generated';
import { STORY_FORM_CONSTS } from './StoryEditor.consts';
import { StoryEditorProps } from './StoryEditorProps';
import { observer } from 'mobx-react-lite';
import {
  BodyDividerWrapper,
  FieldsWrapper,
  HeaderDividerWrapper,
  RHFSearchPublicationWrapper,
  StoryFormWrapper,
  StyledTabs,
} from './StoryEditor.styles';

const StoryEditor: FC<StoryEditorProps> = observer(({ story }) => {
  const [publicationsServer, setPublicationsServer] = useState<Publication[]>([]);
  const [incutsServer, setIncutsServer] = useState<StoriesIncutBlock[]>([]);
  const location = useLocation();
  const [tabValue, setTabValue] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('tab') === 'incuts' ? 1 : 0;
  });
  const history = useHistory();
  const { storiesIncutStore } = useStore();
  const { addingBlocksCache, deletingBlocksCache, setConfirmSaveModalOpen } = storiesIncutStore;
  const [createNewStoryMutation, { loading: createLoading }] = useCreateNewStoryMutation();
  const [updateStoryMutation, { loading: updateLoading }] = useUpdateStoryMutation();
  const [addPublication, { loading: addPublicationLoading }] = useAddPublicationsToStoryMutation();
  const [removePublication, { loading: removePublicationLoading }] = useRemovePublicationsFromStoryMutation();

  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>();
  const [hasNextPage, setHasNextPage] = useState(false);

  const tabNames = [
    { name: STORY_FORM_CONSTS.publicStories, title: STORY_FORM_CONSTS.publicStories },
    { name: STORY_FORM_CONSTS.incutsStories, title: STORY_FORM_CONSTS.incutsStories },
  ];

  const handleTabChange = useCallback(
    (event: React.SyntheticEvent, newValue: number) => {
      // Проверяем, есть ли несохраненные изменения врезок и переключаемся ли на "Публикации" (newValue === 0)
      if (newValue === 0 && (addingBlocksCache.size > 0 || deletingBlocksCache.size > 0)) {
        setConfirmSaveModalOpen(true); // Открываем модалку
        storiesIncutStore.setNextTab(newValue); // Сохраняем целевую вкладку в MobX-хранилище
      } else {
        setTabValue(newValue); // Переключаем вкладку без модалки
      }
    },
    [addingBlocksCache, deletingBlocksCache, setConfirmSaveModalOpen, setTabValue, storiesIncutStore]
  );

  const { fetchMore: fetchMorePublications, refetch: refetchPublications } = usePublicationsByStoryUidQuery({
    variables: { storyUid: story.uid, status: PublicationStatus.PUBLISHED, limit: 10 },
    skip: !story.uid,
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (data.publications) {
        const publications = data?.publications?.edges.map((edge) => edge?.node as Publication) as Publication[];
        setPublicationsServer(publications);
        setValue('publications', publications || []);
        setCursor(data?.publications?.pageInfo.endCursor || undefined);
        setHasNextPage(data?.publications?.pageInfo.hasNextPage);
        return;
      }
      setCursor(undefined);
      setHasNextPage(false);
    },
    onError: (error) => {
      console.error('Publications error:', error.message, error.graphQLErrors);
    },
  });

  const { fetchMore: fetchMoreIncuts, refetch: refetchIncuts } = useStoriesIncutSelectionQuery({
    variables: { storyId: story.uid, status: IncutStatus.Published, limit: 10 },
    skip: !story.uid,
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      console.log(data)
      if (data?.storiesIncutSelection) {
        let incuts = data.storiesIncutSelection.edges.map((edge) => edge.node) as StoriesIncutBlock[];
        const cachedIncuts: StoriesIncutBlock[] = [];
        const cachedUids = Array.from(addingBlocksCache.keys());
        cachedUids.forEach((uid) => {
          const cachedBlock = addingBlocksCache.get(uid) as StoriesIncutBlock | undefined;
          if (cachedBlock && (!deletingBlocksCache || !deletingBlocksCache.has(uid))) {
            cachedIncuts.push(cachedBlock);
          }
        });
        const serverIncuts = incuts.filter((block) => !cachedUids.includes(block.uid));
        incuts = [...cachedIncuts, ...serverIncuts].sort((a, b) => (a.position || 0) - (b.position || 0));
        if (deletingBlocksCache) {
          incuts = incuts.filter((block) => !deletingBlocksCache.has(block.uid));
        }
        setIncutsServer(incuts);
        storiesIncutStore.setStoriesIncutBlocks(incuts);
        storiesIncutStore.setIncutCursor(data.storiesIncutSelection.pageInfo.endCursor || undefined);
        storiesIncutStore.setIncutHasNextPage(data.storiesIncutSelection.pageInfo.hasNextPage);
      } else {
        const cachedIncuts: StoriesIncutBlock[] = [];
        const cachedUids = Array.from(addingBlocksCache.keys());
        cachedUids.forEach((uid) => {
          const cachedBlock = addingBlocksCache.get(uid) as StoriesIncutBlock | undefined;
          if (cachedBlock && (!deletingBlocksCache || !deletingBlocksCache.has(uid))) {
            cachedIncuts.push(cachedBlock);
          }
        });
        const sortedIncuts = cachedIncuts.sort((a, b) => (a.position || 0) - (b.position || 0));
        setIncutsServer(cachedIncuts);
        storiesIncutStore.setStoriesIncutBlocks(cachedIncuts);
        storiesIncutStore.setIncutCursor(undefined);
        console.log('я меняю в первом запросе но если не пришла дата', data)
        storiesIncutStore.setIncutHasNextPage(false);
      }
    },
  });

  const onIncutSave = useCallback(async () => {
    try {
      const { data } = await refetchIncuts({
        storyId: story.uid,
        status: IncutStatus.Published,
        limit: 10,
        fetchPolicy: 'network-only',
      });
      if (data?.storiesIncutSelection) {
        const serverIncuts = data.storiesIncutSelection.edges
          .map((edge) => edge.node)
          .filter((block): block is StoriesIncutBlock => !!block)
          .sort((a, b) => (a.position || 0) - (b.position || 0));
        setIncutsServer(serverIncuts);
        storiesIncutStore.setStoriesIncutBlocks(serverIncuts);
        storiesIncutStore.setIncutCursor(data.storiesIncutSelection.pageInfo.endCursor || undefined);
        storiesIncutStore.setIncutHasNextPage(data.storiesIncutSelection.pageInfo.hasNextPage);
        console.log('Refetched incuts after save:', serverIncuts.map(b => ({
          uid: b.uid,
          position: b.position,
          title: b.title,
          figureVersionSrc: b.figureVersion?.primaryProcessedImage?.url || 'none',
        })));
        serverIncuts.forEach((incut) => {
          if (addingBlocksCache.has(incut.uid)) {
            addingBlocksCache.delete(incut.uid);
          }
        });
      }
    } catch (e) {
      console.error('Error refetching incuts after save:', e);
      toast.error('Error refetching incuts after save:', { containerId: 'error' });
    }
  }, [refetchIncuts, storiesIncutStore, story.uid, addingBlocksCache]);

  useEffect(() => {
    if (tabValue === 0) {
      refetchPublications();
    } else if (tabValue === 1) {
      refetchIncuts().then(({data}) => {
        storiesIncutStore.setIncutHasNextPage(data?.storiesIncutSelection.pageInfo.hasNextPage)
      });
    }
  }, [story.uid, tabValue, refetchPublications, refetchIncuts, storiesIncutStore]);

  const onAdded = useCallback(
    async (publication: Publication) => {
      if (publicationsServer.find((item) => item.uid === publication.uid)) {
        toast.error(STORY_FORM_CONSTS.alreadyAdded, {
          hideProgressBar: true,
          containerId: 'error',
        });
        return;
      }
      const addPublicationResult = await addPublication({
        variables: {
          data: {
            storyUid: story.uid,
            publicationUids: [publication.uid],
          },
        },
      });
      if (!addPublicationResult?.errors?.length) {
        toast.success(STORY_FORM_CONSTS.successAdded, {
          hideProgressBar: true,
          containerId: 'success',
        });
        refetchPublications();
      }
    },
    [story.uid, addPublication, publicationsServer, refetchPublications]
  );

  const onRemove = useCallback(
    async (publicationUid: string) => {
      const removePublicationResult = await removePublication({
        variables: {
          data: {
            storyUid: story.uid,
            publicationUids: [publicationUid],
          },
        },
      });
      if (!removePublicationResult?.errors?.length) {
        toast.success(STORY_FORM_CONSTS.successDeleted, {
          autoClose: 1000,
          hideProgressBar: true,
          containerId: 'success',
        });
        setPublicationsServer((prevState) => prevState.filter((publication) => publication.uid !== publicationUid));
      }
    },
    [story.uid, removePublication]
  );

  const defaultValues = useMemo<StoryEditorFormProps>(
    () => ({
      title: story?.title || '',
      createdAt: story?.createdAt || '',
      description: story?.description || '',
      isActive: story?.isActive || true,
      uid: story?.uid || '',
      updatedAt: story?.updatedAt || '',
      publications: publicationsServer || [],
      figureVersion: (story?.figureVersion || STORY_FORM_CONSTS.figureVersion) as FigureVersion,
      regionUids: story?.regions?.map((region) => region.id) || [],
      onRemove,
      onAdded,
    }),
    [
      story?.title,
      story?.createdAt,
      story?.description,
      story?.isActive,
      story?.uid,
      story?.updatedAt,
      story?.figureVersion,
      story?.regions,
      publicationsServer,
      onAdded,
      onRemove,
    ]
  );

  const { control, setValue, handleSubmit, formState, ...methodsForm } = useForm<StoryEditorFormProps>({
    defaultValues,
  });

  const allFields = useWatch({ control });

  useEffect(() => {
    setValue('publications', publicationsServer);
    setValue('onAdded', onAdded);
    setValue('onRemove', onRemove);
  }, [onAdded, onRemove, publicationsServer, setValue]);

  const onBack = useCallback(() => {
    history.push('/stories');
  }, [history]);

  const onSubmitStory = useCallback(
    async (data) => {
      try {
        if (!data.uid || data.uid.includes('new')) {
          const createNewStoryMutationData = {
            description: data.description,
            figureUid: data.figureVersion?.figure?.uid || null,
            figureVersionUid: data.figureVersion?.uid || null,
            title: data.title,
            status: StoryStatus.Active,
            regionUids: data.regionUids || [],
          };

          await createNewStoryMutation({
            variables: {
              data: createNewStoryMutationData,
            },
          });
          toast.success(STORY_FORM_CONSTS.saved, {
            autoClose: 1000,
            hideProgressBar: true,
            containerId: 'success',
          });
          return;
        }
        await updateStoryMutation({
          variables: {
            data: {
              description: data.description,
              figureVersionUid: data.figureVersion?.uid || null,
              storyUid: story.uid,
              title: data.title,
              regionUids: data.regionUids || [],
            },
          },
        });
        toast.success(STORY_FORM_CONSTS.saved, {
          autoClose: 1000,
          hideProgressBar: true,
          containerId: 'success',
        });
      } catch (e) {
        console.error(e);
        toast.error(STORY_FORM_CONSTS.errorSaving, {
          hideProgressBar: true,
          containerId: 'error',
        });
      }
      onBack();
    },
    [onBack, createNewStoryMutation, updateStoryMutation, story.uid]
  );

  const fetchMorePublicationsData = useCallback(() => {
    void fetchMorePublications({
      variables: {
        first: 10,
        after: cursor,
      },
    });
  }, [cursor, fetchMorePublications]);

  const fetchMoreIncutsData = useCallback(() => {
    if (!storiesIncutStore.incutHasNextPage) return;
    void fetchMoreIncuts({
      variables: {
        first: 10,
        after: storiesIncutStore.incutCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        const newIncuts = fetchMoreResult.storiesIncutSelection.edges.map((edge) => edge.node) as StoriesIncutBlock[];
        const updatedIncuts = [...incutsServer, ...newIncuts];
        const cachedIncuts: StoriesIncutBlock[] = [];
        const cachedUids = Array.from(addingBlocksCache.keys());
        cachedUids.forEach((uid) => {
          const cachedBlock = addingBlocksCache.get(uid) as StoriesIncutBlock | undefined;
          if (cachedBlock && (!deletingBlocksCache || !deletingBlocksCache.has(uid))) {
            cachedIncuts.push(cachedBlock);
          }
        });
        const serverIncuts = updatedIncuts.filter((block) => !cachedUids.includes(block.uid));
        const finalIncuts = [...cachedIncuts, ...serverIncuts].sort((a, b) => (a.position || 0) - (b.position || 0));
        const filteredIncuts = deletingBlocksCache
          ? finalIncuts.filter((block) => !deletingBlocksCache.has(block.uid))
          : finalIncuts;
        setIncutsServer(filteredIncuts);
        storiesIncutStore.setStoriesIncutBlocks(filteredIncuts);
        storiesIncutStore.setIncutCursor(fetchMoreResult.storiesIncutSelection.pageInfo.endCursor || undefined);
        console.log('я меняю в следующем запросе на врезки', fetchMoreResult.storiesIncutSelection.pageInfo.hasNextPage)
        storiesIncutStore.setIncutHasNextPage(fetchMoreResult.storiesIncutSelection.pageInfo.hasNextPage);
        
        return {
          storiesIncutSelection: {
            ...fetchMoreResult.storiesIncutSelection,
            edges: [
              ...(prev.storiesIncutSelection?.edges || []),
              ...(fetchMoreResult.storiesIncutSelection.edges || []),
            ],
          },
        };
      },
    });
  }, [fetchMoreIncuts, incutsServer, storiesIncutStore, addingBlocksCache, deletingBlocksCache]);

  const [publicationsList, setPublications] = useState<Publication[]>([]);

  useEffect(() => {
    setPublications([...publicationsServer]);
  }, [publicationsServer]);

  const [isValidForm, setIsValidForm] = useState<boolean>(false);
  const [buttonDisabledTooltipMessage, setButtonDisabledTooltipMessage] = useState('');

  useEffect(() => {
    let isDirty = false;
    if (
      allFields.title !== defaultValues.title ||
      allFields.description !== defaultValues.description ||
      allFields.figureVersion?.uid !== defaultValues.figureVersion?.uid ||
      JSON.stringify(allFields.regionUids?.sort()) !== JSON.stringify(defaultValues.regionUids?.sort())
    ) {
      isDirty = true;
    }

    if (formState.errors) {
      let resMessage = STORY_FORM_CONSTS.thereAreProblems;
      let hasErrors = false;

      Object.values(formState.errors).forEach((item, ind) => {
        resMessage += `${ind > 0 ? ', ' : ''}${item.message} `;
        hasErrors = true;
      });

      if (!hasErrors) {
        resMessage = STORY_FORM_CONSTS.itNecessaryToFill;
        const emptyFields: string[] = [];

        if (!allFields?.title?.length) {
          emptyFields.push(STORY_FORM_CONSTS.title);
        }
        if (!allFields?.description?.length) {
          emptyFields.push(STORY_FORM_CONSTS.description);
        }
        resMessage += emptyFields.join(', ');
        setButtonDisabledTooltipMessage(
          emptyFields.length > 0 ? resMessage : !isDirty ? STORY_FORM_CONSTS.changesNeeded : ''
        );
      } else {
        setButtonDisabledTooltipMessage(resMessage || '');
      }

      const isValidFormVal: boolean =
        isDirty && allFields?.title?.length && allFields?.description?.length && !hasErrors;

      setIsValidForm(!!isValidFormVal);
      return;
    }

    setIsValidForm(isDirty);
    setButtonDisabledTooltipMessage(isDirty ? '' : STORY_FORM_CONSTS.changesNeeded);
  }, [
    formState,
    allFields.title,
    allFields.description,
    allFields.figureVersion,
    allFields.regionUids,
    defaultValues.title,
    defaultValues.description,
    defaultValues.figureVersion,
    defaultValues.regionUids,
  ]);

  const [isOpenConfirmDialog, setIsOpenConfirmDialog] = useState(false);
  const [publicationUidForDeleting, setPublicationUidForDeleting] = useState('');

  const closeConfirmModal = useCallback(() => {
    onRemove(publicationUidForDeleting);
    setIsOpenConfirmDialog(false);
  }, [publicationUidForDeleting, onRemove]);

  const toggleIsOpenConfirmDialog = useCallback(
    (uid: string) => {
      setPublicationUidForDeleting(uid);
      setIsOpenConfirmDialog(!isOpenConfirmDialog);
    },
    [isOpenConfirmDialog]
  );

  useEffect(() => {
    console.log('раз еблан', hasNextPage)

    console.log('два еблан', storiesIncutStore)


    console.log('три еблан', tabValue)
  }, [hasNextPage, storiesIncutStore, tabValue])

  return (
    <FormProvider {...{ control, setValue, handleSubmit, formState, ...methodsForm }}>
      <StoryFormWrapper>
        <form noValidate onSubmit={handleSubmit(onSubmitStory)}>
          <Header
            title={story.status === StoryStatus.Deleted ? STORY_FORM_CONSTS.storyView : STORY_FORM_CONSTS.storyEditing}
            onBack={onBack}
            hasPreview={false}
            isValidForm={isValidForm}
            buttonDisabledTooltipMessage={buttonDisabledTooltipMessage}
            loading={loading}
          />
          <HeaderDividerWrapper />
          <InfiniteScroll
            dataLength={tabValue === 0 ? publicationsList.length : storiesIncutStore.storiesIncutBlocks.length}
            next={tabValue === 0 ? fetchMorePublicationsData : fetchMoreIncutsData}
            hasMore={tabValue === 0 ? hasNextPage : storiesIncutStore.incutHasNextPage}
            loader={<LinearProgress color="primary" />}
            height="calc(100vh - 160px)"
          >
            {story.status !== StoryStatus.Deleted && (
              <FieldsWrapper>
                <Editor figureTypeSlug="story" />
              </FieldsWrapper>
            )}
            {story.status === StoryStatus.Deleted && (
              <FieldsWrapper>
                <Views figureTypeSlug="story" />
              </FieldsWrapper>
            )}
            <StyledTabs value={tabValue} onChange={handleTabChange} aria-label="story-tabs">
              {tabNames.map((tab, index) => (
                <Tab key={tab.name} label={tab.title} value={index} />
              ))}
            </StyledTabs>
            {story.status !== StoryStatus.Deleted && tabValue === 0 && (
              <RHFSearchPublicationWrapper>
                <RHFSearchPublication />
              </RHFSearchPublicationWrapper>
            )}
            <Box>
              <Box marginTop="24px">
                {tabValue === 0 && (
                  <>
                    {story.publications?.edges.length && publicationsList.length > 0 ? (
                        <>
                          {publicationsList.map((publication) => (
                            <RelatedPublicationItem
                              key={`related-publication}-${publication?.uid}`}
                              publication={publication as Publication}
                              onRemove={toggleIsOpenConfirmDialog}
                            />
                          ))}
                        </>
                      ) : (
                        <>
                          <Box color={grey50}>
                            <Typography variant="text18R">{STORY_FORM_CONSTS.publicationsNotExists}</Typography>
                          </Box>
                          {story.status !== StoryStatus.Deleted && (
                            <Box marginTop="8px" color={grey50}>
                              <Typography variant="text16R">{STORY_FORM_CONSTS.addAtLeastOnePublication}</Typography>
                            </Box>
                          )}
                        </>
                      )}
                  </>
                )}
                {tabValue === 1 && (
                  <Box marginTop="24px">
                    {story.uid ? (
                      <StoryIncutContent
                        storyId={story.uid}
                        storyStatus={story.status}
                        onIncutSave={onIncutSave}
                        setTabValue={setTabValue} 
                      />
                    ) : (
                      <Box color={grey50}>
                        <Typography variant="text18R">Врезок нет</Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </InfiniteScroll>
        </form>
      </StoryFormWrapper>
      <ConfirmationModal
        id="delete-publication-from-story"
        confirmText={STORY_FORM_CONSTS.deletePublicationConfirmationText}
        isOpenConfirmDialog={isOpenConfirmDialog}
        closeConfirmModal={closeConfirmModal}
        okLabel={STORY_FORM_CONSTS.delete}
        toggleIsOpenConfirmDialog={toggleIsOpenConfirmDialog}
        firstSentenceModalConfirmText={STORY_FORM_CONSTS.deletePublicationConfirmationFirstSentenceText}
      />
    </FormProvider>
  );
});

export default StoryEditor;